import { FC, useContext } from "react";
import { GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";

import { isAdmin } from "../../utility/utils";
import { IReturnFlightRecordOptions, IPreferences, IBookingRecordOptions } from "../../logic/api";

import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";

import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";


interface IReturnFlightRecordSettingsProps {
  onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const ReturnFlightRecordSettings: FC<IReturnFlightRecordSettingsProps> = ({ onSave }) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useTranslation();
  const isFlightRecordOptionsReleased = useReleaseManagement(ReleaseFlag.releaseFlightRecordOptions);
  const isRenameReleased = useReleaseManagement(ReleaseFlag.releaseRenameOutboundReturnFlightSettings);
  const isCollectionDeliveryMeetGreetReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreet);
  const isCollectionDeliveryPointsReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryPoints);

  if (!session.current.site) {
    return null;
  }

  if (!isAdmin(session)) {
    return null;
  }

  const labelStyle = isSmallDevice
    ? { textAlign: "center" as const }
    : { paddingTop: 40 };

  const ensureReturnFlightRecordOptions = (data: IPreferences): IReturnFlightRecordOptions => {
    if (!data.returnFlightRecordOptions) {
      data.returnFlightRecordOptions = { properties: {} };
    }

    return data.returnFlightRecordOptions;
  };

  const ensureBookingRecordOptions = (data: IPreferences): IBookingRecordOptions => {
    if (!data.bookingRecordOptions) {
      data.bookingRecordOptions = { properties: {} };
    }

    return data.bookingRecordOptions;
  };

  return (
    <Grid item xs={12}>
      <NotchedCard label={isRenameReleased ? t("settings.returnTime") : t("settings.returnFlightRecOptions")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="returnFlightRecordOptions"
          editType={EditableGroupType.switchableEditing}
          data={session.current.site.preferences}
          onDataChanged={(data) => onSave({
            returnFlightRecordOptions: data.returnFlightRecordOptions,
            ...(isRenameReleased && { booking_returnFlight_enabled: data.booking_returnFlight_enabled }),
            ...((isCollectionDeliveryMeetGreetReleased || isCollectionDeliveryPointsReleased) && { bookingRecordOptions: data.bookingRecordOptions })
          })}
          testId="return-flight-record-options"
        >
          {isRenameReleased && (
            <EditableField<IPreferences, boolean>
              testId="settings-return-time-enabled-switch"
              inputType={EditableFieldTypes.switch}
              label={t("reports.enabled")}
              onProvide={(data) => Boolean(data.booking_returnFlight_enabled)}
              onChange={(data, value) => { data.booking_returnFlight_enabled = value; }}
            />
          )}

          {(!isRenameReleased || Boolean(session.current.site.preferences.booking_returnFlight_enabled)) && (
            <>
              <Grid container>
                <Grid item sm={8} xs={12}>
                  <div style={labelStyle}><strong>{t("flightLookup.labels.flightNumber")}</strong></div>
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.returnFlightRecordOptions?.properties.flightNumber)}
                    onChange={(data, value) => ensureReturnFlightRecordOptions(data).properties.flightNumber = value ? { required: false } : undefined}
                    testId="settings-return-flight-record-flight-number-visible-switch"
                  />
                </Grid>
                <Grid item sm={2} xs={6}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.returnFlightRecordOptions?.properties.flightNumber?.required)}
                    onChange={(data, value) => ensureReturnFlightRecordOptions(data).properties.flightNumber = { required: Boolean(value) }}
                    testId="settings-return-flight-record-flight-number-mandatory-switch"
                  />
                </Grid>
              </Grid>

              {isFlightRecordOptionsReleased && (
                <>
                  <Grid container>
                    <Grid item sm={8} xs={12}>
                      <div style={labelStyle}><strong>{t("booking.labels.returnFlightFrom")}</strong></div>
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.visible")}
                        onProvide={(data) => Boolean(data.returnFlightRecordOptions?.properties.returningFrom)}
                        onChange={(data, value) => ensureReturnFlightRecordOptions(data).properties.returningFrom = value ? { required: false } : undefined}
                        testId="settings-return-flight-record-returning-from-visible-switch"
                      />
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.mandatory")}
                        onProvide={(data) => Boolean(data.returnFlightRecordOptions?.properties.returningFrom?.required)}
                        onChange={(data, value) => ensureReturnFlightRecordOptions(data).properties.returningFrom = { required: Boolean(value) }}
                        testId="settings-return-flight-record-returning-from-mandatory-switch"
                      />
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item sm={8} xs={12}>
                      <div style={labelStyle}><strong>{isRenameReleased ? t("flightLookup.labels.terminal") : t("booking.labels.returnTerminal")}</strong></div>
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.visible")}
                        onProvide={(data) => Boolean(data.returnFlightRecordOptions?.properties.returnTerminal)}
                        onChange={(data, value) => ensureReturnFlightRecordOptions(data).properties.returnTerminal = value ? { required: false } : undefined}
                        testId="settings-return-flight-record-return-terminal-visible-switch"
                      />
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.mandatory")}
                        onProvide={(data) => Boolean(data.returnFlightRecordOptions?.properties.returnTerminal?.required)}
                        onChange={(data, value) => ensureReturnFlightRecordOptions(data).properties.returnTerminal = { required: Boolean(value) }}
                        testId="settings-return-flight-record-return-terminal-mandatory-switch"
                      />
                    </Grid>
                  </Grid>

                  {isCollectionDeliveryPointsReleased && <Grid container>
                    <Grid item sm={8} xs={12}>
                      <div style={labelStyle}><strong>{t("booking.labels.deliveryPoint")}</strong></div>
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.visible")}
                        onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.deliveryPoint)}
                        onChange={(data, value) => ensureBookingRecordOptions(data).properties.deliveryPoint = value ? { required: false } : undefined}
                        testId="settings-return-flight-record-delivery-point-visible-switch"
                      />
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.mandatory")}
                        onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.deliveryPoint?.required)}
                        onChange={(data, value) => ensureBookingRecordOptions(data).properties.deliveryPoint = { required: Boolean(value) }}
                        testId="settings-return-flight-record-delivery-point-mandatory-switch"
                      />
                    </Grid>
                  </Grid>}

                  {isCollectionDeliveryMeetGreetReleased && <Grid container>
                    <Grid item sm={8} xs={12}>
                      <div style={labelStyle}><strong>{t("booking.labels.confirmedReturnDate")}</strong></div>
                    </Grid>
                    <Grid item sm={2} xs={6}>
                      <EditableField<IPreferences, boolean>
                        useFullWidthWhenEditing={false}
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.visible")}
                        onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.confirmedReturnDate)}
                        onChange={(data, value) => ensureBookingRecordOptions(data).properties.confirmedReturnDate = value}
                        testId="settings-return-flight-record-confirmed-return-date-visible-switch"
                      />
                    </Grid>
                  </Grid>}
                </>
              )}
            </>
          )}
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
};
